import { useEffect, useRef, useState } from 'react';
import S from './CircularProgressStyles';

type CircularProgressProps = {
  progress: number;
  initialProgress?: number;
  size?: number;
  strokeWidth?: number;
  showLabel?: boolean;
  labelText?: string;
};

function CircularProgress({
  progress,
  initialProgress,
  size = 150,
  strokeWidth = 8,
  showLabel = true,
  labelText,
}: CircularProgressProps): JSX.Element {
  const center = size / 2;
  const radius = center - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;

  const target = Math.min(100, Math.max(0, progress));
  const init = Math.min(100, Math.max(0, initialProgress ?? 0));
  const [displayed, setDisplayed] = useState(init);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const fromRef = useRef(init);

  useEffect(() => {
    const from = fromRef.current;
    const duration = 500;

    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    startRef.current = null;

    const animate = (timestamp: number): void => {
      if (startRef.current === null) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const t = Math.min(elapsed / duration, 1);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      const current = from + (target - from) * eased;
      setDisplayed(Math.round(current));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        fromRef.current = target;
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); };
  }, [target]);

  const strokeDashoffset = circumference - (circumference * displayed) / 100;

  return (
    <S.Wrapper>
      <div className="circular-progress" style={{ width: size, height: size }}>
        <svg className="circular-progress-circle">
          <circle
            className="circular-progress-background"
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={strokeWidth}
          />
          <circle
            className="circular-progress-value"
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
          />
        </svg>
        <div className="circular-progress-text">{displayed}%</div>
      </div>
      {showLabel && labelText && (
        <div className="circular-progress-label">{labelText}</div>
      )}
    </S.Wrapper>
  );
}

export default CircularProgress;
